import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../context/I18nContext";
import { MEDIA, WHATSAPP_URL, getHomeContent } from "./homeContent";
import LeadForm, { Check, Reassure, track } from "./LeadForm";
import "./Home.css";

// Decorative bar heights for the voice-note waveform.
const WAVE = [
  22, 57, 45, 26, 44, 47, 36, 29, 87, 56, 25, 66, 30, 39, 30, 63, 77, 54, 68,
  75, 53, 23, 31, 61, 53, 83, 60, 40, 68, 26, 25, 24, 77, 32, 36, 76, 30, 31,
  31, 36,
];
const SPEEDS = [1, 1.5, 2];

function formatTime(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function HeroVideo({ c }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const start = () => {
    const video = videoRef.current;
    if (!video || playing) return;
    video.controls = true;
    video.currentTime = 0;
    video.play().catch(() => {});
    setPlaying(true);
    track("ViewContent", { content_name: "extrait_seance" });
  };

  return (
    <div
      className={`hf-vid${playing ? " is-playing" : ""}`}
      onClick={start}
      role={playing ? undefined : "button"}
      tabIndex={playing ? undefined : 0}
      aria-label={playing ? undefined : c.vid.aria}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          start();
        }
      }}
    >
      <video ref={videoRef} className="hf-vid__media" preload="metadata" playsInline src={MEDIA.video} />
      <span className="hf-vid__chip">{c.vid.chip}</span>
      <span className="hf-vid__play" aria-hidden="true">
        <i className="fa-solid fa-play" />
      </span>
      <span className="hf-vid__label">
        <b>{c.vid.title}</b>
        <span>{c.vid.sub}</span>
      </span>
    </div>
  );
}

function VoiceNote({ c }) {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState(MEDIA.audioDuration);
  const [speedIndex, setSpeedIndex] = useState(1);

  const duration = () => {
    const d = audioRef.current?.duration;
    return Number.isFinite(d) && d > 0 ? d : MEDIA.audioDuration;
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (state === "playing" || state === "loading") {
      audio.pause();
      setState("paused");
      return;
    }
    audio.playbackRate = SPEEDS[speedIndex];
    setState("loading");
    audio
      .play()
      .then(() => setState("playing"))
      .catch(() => setState("error"));
    if (state === "idle") track("ViewContent", { content_name: "temoignage_vocal" });
  };

  const cycleSpeed = (event) => {
    event.stopPropagation();
    const next = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };

  const lit = Math.round(progress * WAVE.length);
  const isPlaying = state === "playing" || state === "loading";
  const speedLabel = `${String(SPEEDS[speedIndex]).replace(".", ",")}×`;

  return (
    <div className={`hf-vnote${state === "loading" ? " is-loading" : ""}`} onClick={toggle}>
      <div className="hf-vnote__head">
        <span className="hf-vnote__avatar" aria-hidden="true">
          <i className="fa-solid fa-microphone" />
        </span>
        <span>
          <b>{c.proof.title}</b>
          <span>{c.proof.meta}</span>
        </span>
      </div>
      <div className="hf-vnote__player">
        <button
          type="button"
          className="hf-vnote__play"
          aria-label={isPlaying ? c.proof.pause : c.proof.play}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <i className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play"}`} aria-hidden="true" />
        </button>
        <span className="hf-vnote__wave" aria-hidden="true">
          {WAVE.map((h, i) => (
            <i key={i} className={i < lit ? "on" : ""} style={{ height: `${h}%` }} />
          ))}
        </span>
        <span className="hf-vnote__time">{formatTime(time)}</span>
        <button
          type="button"
          className={`hf-vnote__speed${SPEEDS[speedIndex] > 1 ? " is-fast" : ""}`}
          aria-label={`${c.proof.speed} : ${speedLabel}`}
          onClick={cycleSpeed}
        >
          {speedLabel}
        </button>
      </div>
      <audio
        ref={audioRef}
        preload="none"
        src={MEDIA.audio}
        onTimeUpdate={(e) => {
          const d = duration();
          setProgress(Math.min(1, e.currentTarget.currentTime / d));
          setTime(d - e.currentTarget.currentTime);
        }}
        onEnded={() => {
          setState("idle");
          setProgress(0);
          setTime(duration());
        }}
        onError={() => setState("error")}
      />
      {state === "error" && <p className="hf-vnote__err">{c.proof.err}</p>}
    </div>
  );
}

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hf-faq__item${open ? " is-open" : ""}`}>
      <button type="button" className="hf-faq__q" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {item.q}
        <span className="hf-faq__ic" aria-hidden="true">
          <i className="fa-solid fa-chevron-down" />
        </span>
      </button>
      {open && (
        <div className="hf-faq__a">
          {item.a.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { language } = useTranslation();
  const c = getHomeContent(language);
  const [ctaSource, setCtaSource] = useState("inline");
  const [showSticky, setShowSticky] = useState(false);
  const heroRef = useRef(null);
  const finalRef = useRef(null);

  // Mobile sticky CTA: visible once the hero is gone, hidden over the form.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const seen = { hero: true, final: false };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        seen[entry.target === heroRef.current ? "hero" : "final"] = entry.isIntersecting;
      });
      setShowSticky(!seen.hero && !seen.final);
    });
    if (heroRef.current) observer.observe(heroRef.current);
    if (finalRef.current) observer.observe(finalRef.current);
    return () => observer.disconnect();
  }, []);

  const book = (source) => {
    setCtaSource(source);
    track("InitiateCheckout", { content_name: "seance_gratuite", cta_source: source });
    finalRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="hf-page">
      <section className="hf-hero" ref={heroRef}>
        <div className="hf-wrap hf-hero__grid">
          <div>
            <h1>
              {c.hero.titleStart}
              <span className="hf-grad">{c.hero.titleHighlight}</span>
              {c.hero.titleEnd}
            </h1>
            <p className="hf-lead">
              {c.hero.lead} <b>{c.hero.leadBold}</b>
            </p>
            <button type="button" className="hf-btn hf-btn--primary hf-btn--block hf-hero__cta" onClick={() => book("hero")}>
              {c.cta.book}
              <i className="fa-solid fa-arrow-right hf-flip" aria-hidden="true" />
            </button>
            <Reassure items={[c.r.nocard, c.r.nocommit, c.r.s40]} align="start" />
          </div>
          <div>
            <HeroVideo c={c} />
            <div className="hf-qualif">
              {c.qualif.map((item, i) => (
                <span key={item}>
                  <i className={`fa-solid ${["fa-child", "fa-seedling", "fa-moon"][i]}`} aria-hidden="true" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="hf-section hf-recog">
        <div className="hf-wrap">
          <div className="hf-head">
            <span className="hf-eyebrow">{c.recog.eye}</span>
            <h2>{c.recog.h2}</h2>
          </div>
          <div className="hf-recog__grid">
            {c.recog.cards.map((card) => (
              <div className="hf-rcard" key={card}>
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                <b>{card}</b>
              </div>
            ))}
          </div>
          <div className="hf-recog__out">
            <b>{c.recog.outT}</b>
            <span>{c.recog.outS}</span>
          </div>
        </div>
      </section>

      <section className="hf-section hf-proof" id="avis">
        <div className="hf-wrap">
          <div className="hf-head">
            <span className="hf-eyebrow">{c.proof.eye}</span>
            <h2>{c.proof.h2}</h2>
            <p className="hf-lead">{c.proof.sub}</p>
          </div>
          <VoiceNote c={c} />
        </div>
      </section>

      <section className="hf-section hf-steps">
        <div className="hf-wrap">
          <div className="hf-head">
            <span className="hf-eyebrow">{c.steps.eye}</span>
            <h2>{c.steps.h2}</h2>
          </div>
          <div className="hf-steps__grid">
            {c.steps.items.map((item, index) => (
              <article className="hf-scard" key={item.title}>
                <div className="hf-scard__num">{index + 1}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <span className="hf-scard__meta">{item.meta}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="hf-section hf-offer" id="offre">
        <div className="hf-wrap">
          <div className="hf-head">
            <span className="hf-eyebrow">{c.offer.eye}</span>
            <h2>{c.offer.h2}</h2>
            <p className="hf-lead">{c.offer.sub}</p>
          </div>

          <div className="hf-incl">
            <b>{c.offer.inclT}</b>
            <ul>
              {c.offer.incl.map((item) => (
                <li key={item}>
                  <i className="fa-solid fa-check" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="hf-pgrid">
            {c.offer.plans.map((plan) => (
              <article className={`hf-pcard${plan.badge ? " is-best" : ""}`} key={plan.id}>
                {plan.badge && <span className="hf-pcard__badge">{plan.badge}</span>}
                <div className="hf-pcard__head">
                  <b>{plan.name}</b>
                  <span>{plan.freq}</span>
                </div>
                <p className="hf-pcard__what">
                  {plan.what[0]}
                  <b>{plan.what[1]}</b>
                  {plan.what[2]}
                </p>
                <p className="hf-pcard__price">
                  <s>{plan.old}</s> <b>{plan.price}</b>
                  <small>{c.offer.perMonth}</small>
                </p>
                <p className={`hf-pcard__unit${plan.bestUnit ? " is-best" : ""}`}>
                  {plan.unit[0]}
                  <b>{plan.unit[1]}</b>
                  {plan.unit[2]}
                </p>
                <button
                  type="button"
                  className={`hf-btn hf-btn--block ${plan.badge ? "hf-btn--primary" : "hf-btn--ghost"}`}
                  onClick={() => book(`offre-${plan.id}`)}
                >
                  {c.offer.planCta}
                </button>
              </article>
            ))}
          </div>
          <p className="hf-pnote">{c.offer.note}</p>

          <div className="hf-pextra">
            {c.offer.extras.map((extra) => (
              <div className="hf-pbox" key={extra.title}>
                <b>{extra.title}</b>
                <span>{extra.text}</span>
              </div>
            ))}
          </div>

          <div className="hf-ocard">
            <div className="hf-guarantee">
              <i className="fa-solid fa-shield-heart" aria-hidden="true" />
              <div>
                <b>{c.offer.gT}</b>
                <span>{c.offer.gS}</span>
              </div>
            </div>
            <button type="button" className="hf-btn hf-btn--primary hf-btn--block" onClick={() => book("offre")}>
              {c.offer.cta}
            </button>
            <Reassure items={[c.r.free, c.r.nocard, c.r.nocommit]} />
          </div>

          <div className="hf-compare">
            {c.offer.compare.map((row) => (
              <div className={`hf-crow${row.win ? " is-win" : ""}`} key={row.title}>
                <div>
                  <b>{row.title}</b>
                  <span>{row.text}</span>
                </div>
                <span className={`hf-cmark ${row.win ? "is-yes" : "is-no"}`}>{row.mark}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="hf-section hf-faq" id="faq">
        <div className="hf-wrap">
          <div className="hf-head">
            <span className="hf-eyebrow">{c.faq.eye}</span>
            <h2>{c.faq.h2}</h2>
          </div>
          <div className="hf-faq__list">
            {c.faq.items.map((item) => (
              <FaqItem item={item} key={item.q} />
            ))}
          </div>
        </div>
      </section>

      <section className="hf-section hf-final" id="reserver" ref={finalRef}>
        <div className="hf-wrap hf-final__inner">
          <div>
            <span className="hf-eyebrow hf-eyebrow--light">{c.final.eye}</span>
            <h2>{c.final.h2}</h2>
            <p className="hf-lead">{c.final.lead}</p>
            <ul className="hf-fbullets">
              {c.final.bullets.map((item) => (
                <li key={item}>
                  <Check />
                  {item}
                </li>
              ))}
            </ul>
            <p className="hf-final__wa">
              {c.final.waQ}{" "}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("Contact", { method: "whatsapp", emplacement: "cta_final" })}
              >
                {c.final.waLink}
              </a>
              .
            </p>
          </div>
          <LeadForm c={c} language={language} ctaSource={ctaSource} />
        </div>
      </section>

      <div className={`hf-sticky${showSticky ? " is-visible" : ""}`}>
        <button type="button" className="hf-btn hf-btn--primary hf-btn--block" onClick={() => book("sticky")}>
          {c.cta.book}
        </button>
      </div>
    </main>
  );
}
